import React, { useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  StatusBar,
  TouchableOpacity,
  Modal,
} from "react-native";
import { FontFamilies } from "@/constants/Typography";
import { ActionButton } from "@/components/ActionButton";
import { router, Stack } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import Icons from "@/assets/icons";
import { useGmailLinkWebReturn } from "@/hooks/useGmailLinkWebReturn";
import AuthServices from "@/services/authServices";
import { showError, showSuccess } from "@/utils/toast";
import { Image } from "expo-image";

const outlookIcon = require("@/assets/images/outlook.png");
const yahooIcon = require("@/assets/images/yahoo.png");

// Connection icons row component for modals
const ConnectionIconsRow = ({ emailIcon }: { emailIcon: React.ReactNode }) => (
  <View style={styles.connectionIconsRow}>
    {/* Sparrow Logo with green gradient background */}
    <View style={styles.sparrowLogoContainer}>
      <Icons.Logo width={36} height={36} />
    </View>

    {/* Link/Connect arrows */}
    <Icons.LinkConnect width={41} height={36} />

    {/* Email type icon */}
    <View style={styles.emailIconContainer}>{emailIcon}</View>
  </View>
);

type LinkedEmails = {
  gmail: boolean;
  outlook: boolean;
  yahoo: boolean;
};

const ConnectEmailScreen = () => {
  const [isLinking, setIsLinking] = useState(false);
  const [linkedEmails, setLinkedEmails] = useState<LinkedEmails>({
    gmail: false,
    outlook: false,
    yahoo: false,
  });
  const [isGmailSheetVisible, setIsGmailSheetVisible] = useState(false);
  const [isOutlookSheetVisible, setIsOutlookSheetVisible] = useState(false);
  const [isYahooSheetVisible, setIsYahooSheetVisible] = useState(false);

  useGmailLinkWebReturn({
    onSuccess: () => {
      setLinkedEmails((prev) => ({ ...prev, gmail: true }));
      setIsGmailSheetVisible(false);
    },
  });

  const hasAnyLinkedEmail =
    linkedEmails.gmail || linkedEmails.outlook || linkedEmails.yahoo;

  const handleOpenGmailSheet = () => {
    setIsGmailSheetVisible(true);
  };

  const handleCloseGmailSheet = () => {
    setIsGmailSheetVisible(false);
  };

  const handleConnectGmail = async () => {
    if (isLinking) return;

    setIsLinking(true);
    try {
      const res = await AuthServices.handleGoogleAuth();
      if ("handoff" in res && res.handoff === "redirect") {
        return;
      }
      if (res.success) {
        showSuccess("Gmail linked successfully!");
        setLinkedEmails((prev) => ({ ...prev, gmail: true }));
        setIsGmailSheetVisible(false);
      } else {
        showError("Failed to link Gmail");
      }
    } catch (error) {
      console.log("error", error);
      showError("Failed to link Gmail");
    } finally {
      setIsLinking(false);
    }
  };

  const handleOpenOutlookSheet = () => {
    setIsOutlookSheetVisible(true);
  };

  const handleCloseOutlookSheet = () => {
    setIsOutlookSheetVisible(false);
  };

  const handleConnectOutlook = async () => {
    if (isLinking) return;

    setIsLinking(true);
    try {
      const res = await AuthServices.handleOutlookAuth();
      if (res.success) {
        showSuccess("Outlook linked successfully!");
        setLinkedEmails((prev) => ({ ...prev, outlook: true }));
        setIsOutlookSheetVisible(false);
      } else {
        showError("Failed to link Outlook");
      }
    } catch {
      showError("Failed to link Outlook");
    } finally {
      setIsLinking(false);
    }
  };

  const handleOpenYahooSheet = () => {
    setIsYahooSheetVisible(true);
  };

  const handleCloseYahooSheet = () => {
    setIsYahooSheetVisible(false);
  };

  const handleConnectYahoo = async () => {
    if (isLinking) return;

    setIsLinking(true);
    try {
      const res = await AuthServices.handleYahooAuth();
      if (res.success) {
        showSuccess("Yahoo linked successfully!");
        setLinkedEmails((prev) => ({ ...prev, yahoo: true }));
        setIsYahooSheetVisible(false);
      } else {
        showError("Failed to link Yahoo");
      }
    } catch {
      showError("Failed to link Yahoo");
    } finally {
      setIsLinking(false);
    }
  };

  const handleContinue = () => {
    router.push("/signup/enable-money-tracking");
  };

  const handleSkip = () => {
    router.push("/signup/enable-money-tracking");
  };

  return (
    <View style={styles.container}>
      <Stack.Screen options={{ headerShown: false, gestureEnabled: false }} />
      <StatusBar barStyle="dark-content" />
      <SafeAreaView style={styles.safeArea}>
        {/* Progress Bar */}
        <View style={styles.progressContainer}>
          <View style={styles.progressBar}>
            <View style={styles.progressFill} />
          </View>
        </View>

        {/* Content */}
        <View style={styles.content}>
          {/* Header */}
          <View style={styles.headerSection}>
            <Text style={styles.sectionLabel}>AI refund assistant</Text>
            <Text style={styles.title}>Connect your email</Text>
            <Text style={styles.subtitle}>
              This helps our AI detect refund opportunities related to your
              purchases and subscriptions.
            </Text>
          </View>

          {/* Email Options */}
          <View style={styles.emailOptions}>
            <TouchableOpacity
              style={styles.emailOption}
              onPress={handleOpenGmailSheet}
              disabled={isLinking || linkedEmails.gmail}
            >
              <Icons.Gmail width={29} height={29} />
              <Text style={styles.emailOptionText}>Gmail</Text>
              {linkedEmails.gmail ? (
                <View style={styles.connectedBadge}>
                  <Ionicons name="link" size={16} color="#0F9129" />
                  <Text style={styles.connectedText}>Connected</Text>
                </View>
              ) : (
                <Ionicons name="arrow-forward" size={24} color="#161D14" />
              )}
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.emailOption}
              onPress={handleOpenOutlookSheet}
              disabled={isLinking || linkedEmails.outlook}
            >
              <Image source={outlookIcon} style={styles.emailIcon} />
              <Text style={styles.emailOptionText}>
                Outlook / Live / Hotmail
              </Text>
              {linkedEmails.outlook ? (
                <View style={styles.connectedBadge}>
                  <Ionicons name="link" size={16} color="#0F9129" />
                  <Text style={styles.connectedText}>Connected</Text>
                </View>
              ) : (
                <Ionicons name="arrow-forward" size={24} color="#161D14" />
              )}
            </TouchableOpacity>

            {/* TODO: Re-enable Yahoo button when Yahoo auth is ready
            <TouchableOpacity
              style={styles.emailOption}
              onPress={handleOpenYahooSheet}
              disabled={isLinking || linkedEmails.yahoo}
            >
              <Image source={yahooIcon} style={styles.yahooIcon} contentFit="contain" />
              <Text style={styles.emailOptionText}>Yahoo Mail</Text>
              {linkedEmails.yahoo ? (
                <View style={styles.connectedBadge}>
                  <Ionicons name="link" size={16} color="#0F9129" />
                  <Text style={styles.connectedText}>Connected</Text>
                </View>
              ) : (
                <Ionicons name="arrow-forward" size={24} color="#161D14" />
              )}
            </TouchableOpacity>
            */}

            <Text style={styles.disclaimer}>
              Sparrow only scans for relevant refund-related information
            </Text>
          </View>
        </View>

        {/* Spacer */}
        <View style={styles.spacer} />

        {/* Bottom Section */}
        <View style={styles.bottomSection}>
          <View style={styles.privacyRow}>
            <Icons.ShieldCheck width={18} height={18} />
            <Text style={styles.privacyText}>
              We do not sell your data.{"\n"}This is only required to perform
              the search.
            </Text>
          </View>
          <View style={styles.buttonContainer}>
            <ActionButton
              title="Continue"
              onPress={handleContinue}
              disabled={!hasAnyLinkedEmail}
              style={!hasAnyLinkedEmail ? styles.disabledButton : undefined}
            />
          </View>
          <TouchableOpacity onPress={handleSkip}>
            <Text style={styles.skipText}>I&apos;ll do this later</Text>
          </TouchableOpacity>
        </View>

        {/* Gmail Connection Modal */}
        <Modal
          visible={isGmailSheetVisible}
          transparent
          animationType="fade"
          onRequestClose={handleCloseGmailSheet}
        >
          <View style={styles.modalOverlay}>
            <View style={styles.modalContent}>
              {/* Title */}
              <Text style={styles.modalTitle}>Connect to Gmail</Text>

              {/* Connection Icons Row */}
              <ConnectionIconsRow
                emailIcon={<Icons.Gmail width={64} height={64} />}
              />

              {/* Description */}
              <Text style={styles.modalDescription}>
                Connecting allows Sparrow products to interact with Gmail data
                on your behalf. This may include read, write, modify and delete
                data depending on your Gmail permissions.
              </Text>

              {/* Connect Button */}
              <ActionButton
                title={isLinking ? "Connecting..." : "Connect"}
                onPress={handleConnectGmail}
                disabled={isLinking}
              />
            </View>
          </View>
        </Modal>

        {/* Outlook Connection Modal */}
        <Modal
          visible={isOutlookSheetVisible}
          transparent
          animationType="fade"
          onRequestClose={handleCloseOutlookSheet}
        >
          <View style={styles.modalOverlay}>
            <View style={styles.modalContent}>
              {/* Title */}
              <Text style={styles.modalTitle}>Connect to Outlook</Text>

              {/* Connection Icons Row */}
              <ConnectionIconsRow
                emailIcon={
                  <Image
                    source={outlookIcon}
                    style={styles.modalEmailIcon}
                    contentFit="contain"
                  />
                }
              />

              {/* Description */}
              <Text style={styles.modalDescription}>
                Connecting allows Sparrow products to interact with Outlook data
                on your behalf. This may include read, write, modify and delete
                data depending on your Outlook permissions.
              </Text>

              {/* Connect Button */}
              <ActionButton
                title={isLinking ? "Connecting..." : "Connect"}
                onPress={handleConnectOutlook}
                disabled={isLinking}
              />
            </View>
          </View>
        </Modal>

        {/* Yahoo Connection Modal */}
        <Modal
          visible={isYahooSheetVisible}
          transparent
          animationType="fade"
          onRequestClose={handleCloseYahooSheet}
        >
          <View style={styles.modalOverlay}>
            <View style={styles.modalContent}>
              {/* Title */}
              <Text style={styles.modalTitle}>Connect to Yahoo Mail</Text>

              {/* Connection Icons Row */}
              <ConnectionIconsRow
                emailIcon={
                  <Image
                    source={yahooIcon}
                    style={styles.modalEmailIcon}
                    contentFit="contain"
                  />
                }
              />

              {/* Description */}
              <Text style={styles.modalDescription}>
                Connecting allows Sparrow products to interact with Yahoo Mail
                data on your behalf. This may include read, write, modify and
                delete data depending on your Yahoo permissions.
              </Text>

              {/* Connect Button */}
              <ActionButton
                title={isLinking ? "Connecting..." : "Connect"}
                onPress={handleConnectYahoo}
                disabled={isLinking}
              />
            </View>
          </View>
        </Modal>
      </SafeAreaView>
    </View>
  );
};

export default ConnectEmailScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  safeArea: {
    flex: 1,
  },
  progressContainer: {
    paddingHorizontal: 24,
    paddingTop: 10,
    paddingBottom: 16,
  },
  progressBar: {
    height: 6,
    backgroundColor: "#E1EFE4",
    borderRadius: 100,
  },
  progressFill: {
    width: 16,
    height: 6,
    backgroundColor: "rgba(22, 29, 20, 0.5)",
    borderRadius: 100,
  },
  content: {
    flex: 1,
    paddingHorizontal: 16,
  },
  headerSection: {
    gap: 12,
    marginBottom: 24,
  },
  sectionLabel: {
    fontFamily: FontFamilies.Inter.Medium,
    fontSize: 16,
    lineHeight: 22,
    color: "#0F9129",
    letterSpacing: -0.32,
  },
  title: {
    fontFamily: FontFamilies.Inter.SemiBold,
    fontSize: 32,
    lineHeight: 38,
    color: "#161D14",
    letterSpacing: -0.64,
  },
  subtitle: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 16,
    lineHeight: 24,
    color: "rgba(22, 29, 20, 0.7)",
    letterSpacing: 0.16,
  },
  emailOptions: {
    gap: 10,
  },
  emailOption: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F3F3F3",
    borderRadius: 8,
    padding: 16,
    gap: 16,
  },
  emailIcon: {
    width: 29,
    height: 29,
  },
  yahooIcon: {
    width: 29,
    height: 22,
  },
  emailOptionText: {
    flex: 1,
    fontFamily: FontFamilies.Inter.Medium,
    fontSize: 16,
    lineHeight: 22,
    color: "#161D14",
    letterSpacing: -0.32,
  },
  disclaimer: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 13,
    lineHeight: 19,
    color: "#414141",
  },
  spacer: {
    flex: 1,
  },
  bottomSection: {
    paddingHorizontal: 16,
    borderTopWidth: 1,
    borderTopColor: "#F3F3F3",
    paddingTop: 12,
  },
  privacyRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 6,
    marginBottom: 12,
  },
  privacyText: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 14,
    lineHeight: 20,
    color: "#000000",
    letterSpacing: 0.14,
  },
  buttonContainer: {
    marginBottom: 12,
  },
  disabledButton: {
    backgroundColor: "#CCCCCC",
  },
  skipText: {
    fontFamily: FontFamilies.Inter.Medium,
    fontSize: 14,
    lineHeight: 20,
    color: "#0F9129",
    textAlign: "center",
    letterSpacing: -0.28,
    marginBottom: 12,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 24,
  },
  modalContent: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    padding: 24,
    width: "100%",
    gap: 16,
  },
  modalTitle: {
    fontFamily: FontFamilies.Inter.SemiBold,
    fontSize: 20,
    lineHeight: 28,
    color: "#161D14",
    textAlign: "center",
  },
  connectionIconsRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 24,
  },
  sparrowLogoContainer: {
    width: 56,
    height: 56,
    borderRadius: 12,
    backgroundColor: "#369449",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  emailIconContainer: {
    width: 64,
    height: 64,
    alignItems: "center",
    justifyContent: "center",
  },
  modalEmailIcon: {
    width: 64,
    height: 64,
  },
  modalDescription: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 12,
    lineHeight: 18,
    color: "#908F8F",
    textAlign: "center",
  },
  connectedBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  connectedText: {
    fontFamily: FontFamilies.Inter.Medium,
    fontSize: 13,
    lineHeight: 19,
    color: "#0F9129",
  },
});
